// FILE: src/services/activation/activationService.js
//
// Read-only client wrapper over the Journey Activation intervention RPCs
// (migrations 089 + 091). Activation interventions are the deliberate nudges we
// send to registered-but-dormant learners; intervention #1 is the inactive-24h
// lifecycle email.
//
// The RPCs enforce _is_caller_admin() server-side, so a non-admin caller gets
// an error and null is returned here.

import { supabase } from '../supabaseClient';

/**
 * The natural activation baseline: the rate at which registrations still
 * dormant 24 hours after signing up come back and answer a first question
 * within the next 7 days with NO nudge at all.
 *
 * Measured on production 2026-08-19, immediately before intervention #1 was
 * enabled: 7 of 66, median 24.4h. This is a clean-room reading that cannot be
 * re-taken now the intervention is live, which is exactly why it is pinned in
 * code rather than recomputed. See scripts/activation-intervention-baseline.sql
 * and docs/activation-intervention-1-enablement.md.
 *
 * The denominator is the dormant population, i.e. everyone who WOULD have been
 * targeted - so the comparable intervention metric is answered_first/targeted,
 * not answered_first/delivered.
 */
export const NATURAL_ACTIVATION_BASELINE = {
  pct: 10.6,
  numerator: 7,
  denominator: 66,
  measuredOn: '2026-08-19',
  windowDays: 7,
};

/**
 * Below this many MATURED learners, small-sample volatility swamps any real
 * effect and the comparison against the baseline should not be read as a
 * result.
 *
 * The threshold counts learners whose attribution window has CLOSED, not every
 * learner ever targeted — a learner still inside their 7 days has not yet had
 * the chance to activate and must not be scored as a failure.
 *
 * NOTE: an earlier comment here estimated "~5 eligible/day, roughly two weeks
 * of accrual". That estimate came from the one-off 2026-08-19 backlog drain and
 * was retired on 2026-08-30. Observed steady-state accrual is ~0.4-0.5/day, so
 * this threshold is not reachable on a useful near-term horizon and no calendar
 * date should be attached to it. The 24h nudge is judged on operational health;
 * a second-touch decision needs its own evaluation design, not this cohort.
 */
export const MIN_COHORT_FOR_READ = 70;

/**
 * Return-signal provenance, surfaced so an operator can tell "nobody came back"
 * apart from "the instrument did not exist yet".
 *
 * Until migration 093 the funnel derived `returned` from the `app_open`
 * telemetry event joined on user_id. Every app_open row carries a NULL user_id
 * — initTelemetry() emits it synchronously while the auth session is still
 * resolving — so `returned` was structurally zero for every cohort ever
 * measured. It was never a behavioural reading.
 *
 * The replacement signal is `authenticated_open`, emitted once per app session
 * per user after identity resolves, covering both already-authenticated and
 * newly-authenticated returns. Historical telemetry was deliberately NOT
 * rewritten and NULL app_open rows were NOT backfilled.
 */
export const RETURN_SIGNAL = {
  event: 'authenticated_open',
  fixedInMigration: '093',
  legacyEvent: 'app_open',
  legacyProxyEvent: 'signin',
};

/** Interventions in the activation model, in rollout order. */
export const INTERVENTIONS = [
  {
    key: 'inactive_24h',
    label: 'Inactive 24h reminder',
    blurb: 'Email nudge 24 hours after a registration that never answered a question.',
  },
  {
    key: 'inactive_4d',
    label: 'Inactive 4-day reminder',
    blurb: 'Second nudge at 4 days. Held until the 24-hour window proves it adds value.',
  },
];

/**
 * Fetch the funnel for one intervention.
 *
 * @param {object} opts
 * @param {string} [opts.journey='inactive_24h']
 * @param {string} [opts.from]  ISO date (defaults server-side to 30 days ago)
 * @param {string} [opts.to]    ISO date (defaults server-side to today)
 * @param {number} [opts.windowDays=7] attribution window after each send
 * @returns {Promise<object|null>}
 */
export async function fetchActivationIntervention(opts = {}) {
  const { journey = 'inactive_24h', from, to, windowDays } = opts;
  const args = { p_journey: journey };
  if (from) args.p_from = from;
  if (to) args.p_to = to;
  if (windowDays) args.p_window_days = windowDays;

  try {
    const { data, error } = await supabase.rpc('admin_activation_intervention', args);
    if (error) {
      console.warn('[activationService] admin_activation_intervention failed:', error.message);
      return null;
    }
    return data;
  } catch (err) {
    console.warn('[activationService] unexpected error:', err?.message || err);
    return null;
  }
}

/**
 * Fetch eligibility-scheduler health for one environment.
 *
 * The sweep is the only thing that enqueues inactive reminders. If its cron
 * stops, nothing errors - volume just quietly falls - so this is surfaced
 * beside the funnel rather than buried in delivery health.
 *
 * @param {string} [environment='prod']
 * @returns {Promise<object|null>}
 */
export async function fetchSchedulerHealth(environment = 'prod') {
  try {
    const { data, error } = await supabase.rpc('admin_eligibility_scheduler_health', {
      p_environment: environment,
    });
    if (error) {
      console.warn('[activationService] admin_eligibility_scheduler_health failed:', error.message);
      return null;
    }
    return data;
  } catch (err) {
    console.warn('[activationService] unexpected error:', err?.message || err);
    return null;
  }
}

/**
 * Percentage helper for funnel rows. Returns null (not 0) when the denominator
 * is zero so the UI can render an em dash instead of a misleading 0%.
 */
export function rate(numerator, denominator) {
  const n = Number(numerator); const d = Number(denominator);
  if (!Number.isFinite(n) || !Number.isFinite(d) || d <= 0) return null;
  return Math.round((n / d) * 1000) / 10;
}

/**
 * The headline activation rate: matured_answered_first over MATURED_TARGETED.
 *
 * Two deliberate choices in that denominator:
 *
 *   • TARGETED, not delivered. The baseline this is compared against counts
 *     every dormant learner who would have been reminded. Measuring over
 *     delivered silently drops the learners a delivery failure removed from the
 *     experiment, which flatters the intervention exactly when it is doing
 *     worst.
 *   • MATURED, not everyone. A learner three days into a seven-day window has
 *     not failed to activate; they have not finished. Counting them in the
 *     denominator drags the rate down by exactly the share of the cohort that
 *     is still in flight.
 */
export function activationRate(data) {
  if (!data) return null;
  return rate(data.matured_answered_first, data.matured_targeted);
}

/**
 * Operational (all-targeted) rate. Shown beside the efficacy rate for
 * completeness, never compared to the baseline.
 */
export function operationalActivationRate(data) {
  if (!data) return null;
  return rate(data.answered_first, data.targeted);
}

/**
 * True when some send in this cohort predates the authenticated_open
 * instrument, so its authenticated-return figures under-read and the sign-in
 * proxy is the only available (lower-bound) reading.
 */
export function returnsPredateInstrument(data) {
  return Boolean(data?.instrumentation?.cohort_predates_fix);
}

/**
 * Compare the intervention against the pinned natural baseline.
 *
 * Returns { pct, baselinePct, deltaPts, verdict, cohort } where verdict is:
 *   'too-early'              cohort below MIN_COHORT_FOR_READ - not a result yet
 *   'above'/'below'/'level'  once the cohort is big enough to mean something
 *   null                     nothing matured yet
 *
 * `cohort` is the MATURED count. A cohort of zero matured learners returns a
 * null verdict rather than 0%, so a screen full of in-flight sends never reads
 * as a failed intervention.
 */
export function compareToBaseline(data) {
  const cohort = Number(data?.matured_targeted || 0);
  const pct = activationRate(data);
  if (!cohort || pct === null) {
    return {
      pct: null,
      baselinePct: NATURAL_ACTIVATION_BASELINE.pct,
      deltaPts: null,
      verdict: null,
      cohort,
    };
  }
  const baselinePct = NATURAL_ACTIVATION_BASELINE.pct;
  const deltaPts = Math.round((pct - baselinePct) * 10) / 10;
  let verdict;
  if (cohort < MIN_COHORT_FOR_READ) {
    verdict = 'too-early';
  } else if (deltaPts > 0) {
    verdict = 'above';
  } else if (deltaPts < 0) {
    verdict = 'below';
  } else {
    verdict = 'level';
  }
  return { pct, baselinePct, deltaPts, verdict, cohort };
}
